import { uniq } from 'es-toolkit';
import React from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import { Insertion, LayoutModel, ModelCategory } from '@paragon/contract-library';
import { assertExhaustiveSwitch, naturalSortByProperty } from '@paragon/utilities';

import { useAppSelector } from '../../hooks';

interface Props {
  readonly project: LayoutModel;
  readonly selectedModelGuids: readonly string[];
  readonly setSelectedModelGuids: (selectedModelGuids: readonly string[]) => void;
}

export function ProjectHierarchyCheckboxTree(props: Props) {
  const layoutModels = useAppSelector((state) => state.layoutModels);
  const insertions = useAppSelector((state) => state.insertions);

  const selectedItemsState = calculateSelectedItemsState(
    props.project,
    props.selectedModelGuids,
    layoutModels,
    insertions,
  );

  function renderModelAndChildren(model: LayoutModel, itemId: string) {
    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <FormControlLabel
            label={getLabelForModel(model)}
            control={
              <Checkbox
                sx={{ py: 0, px: 1 }}
                checked={selectedItemsState[itemId].checked}
                indeterminate={selectedItemsState[itemId].indeterminate}
                onChange={() => {
                  const modelsToUpdate = getAllDescendantsOfLayoutModel(model, layoutModels, insertions);
                  modelsToUpdate.add(model.guid);

                  props.setSelectedModelGuids(
                    selectedItemsState[itemId].checked
                      ? props.selectedModelGuids.filter((modelGuid) => !modelsToUpdate.has(modelGuid))
                      : uniq([...props.selectedModelGuids, ...modelsToUpdate]),
                  );
                }}
              />
            }
          />
        }
      >
        {modelCanHaveChildren(model) ? (
          <TreeItem
            itemId={`${itemId}-defaults`}
            label={
              <FormControlLabel
                label="(defaults)"
                control={
                  <Checkbox
                    sx={{ py: 0, px: 1 }}
                    checked={selectedItemsState[`${itemId}-defaults`].checked}
                    indeterminate={selectedItemsState[`${itemId}-defaults`].indeterminate}
                    onChange={() => {
                      props.setSelectedModelGuids(
                        selectedItemsState[`${itemId}-defaults`].checked
                          ? props.selectedModelGuids.filter((modelGuid) => modelGuid !== model.guid)
                          : [...props.selectedModelGuids, model.guid],
                      );
                    }}
                  />
                }
              />
            }
          />
        ) : null}

        {naturalSortByProperty(getChildren(model, layoutModels, insertions), (model) => model.name).map((child) =>
          renderModelAndChildren(child, `${itemId}-${child.guid}`),
        )}
      </TreeItem>
    );
  }

  return (
    <SimpleTreeView
      disableSelection
      expansionTrigger="iconContainer"
      defaultExpandedItems={Array.from(calculateItemIdsWithChildren(props.project, layoutModels, insertions))}
    >
      {renderModelAndChildren(props.project, props.project.guid)}
    </SimpleTreeView>
  );
}

interface SelectedState {
  readonly checked: boolean;
  readonly indeterminate: boolean;
}

interface SelectedItemsState {
  readonly [itemId: string]: SelectedState;
}

function calculateSelectedItemsState(
  project: LayoutModel,
  selectedModelGuids: readonly string[],
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
): SelectedItemsState {
  const result: { [itemId: string]: SelectedState } = {};
  calculateSelectedItemsStateForModel(project, project.guid, selectedModelGuids, allModels, insertions, result);
  return result;
}

function calculateSelectedItemsStateForModel(
  model: LayoutModel,
  itemId: string,
  selectedModelGuids: readonly string[],
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
  result: { [itemId: string]: SelectedState },
) {
  if (!modelCanHaveChildren(model)) {
    result[itemId] = { checked: selectedModelGuids.includes(model.guid), indeterminate: false };
    return;
  }

  const modelDefaultsItemId = `${itemId}-defaults`;
  result[modelDefaultsItemId] = { checked: selectedModelGuids.includes(model.guid), indeterminate: false };

  let someChildrenAreChecked = result[modelDefaultsItemId].checked;
  let allChildrenAreChecked = result[modelDefaultsItemId].checked;
  for (const child of getChildren(model, allModels, insertions)) {
    const childItemId = `${itemId}-${child.guid}`;
    if (!result[childItemId]) {
      calculateSelectedItemsStateForModel(child, childItemId, selectedModelGuids, allModels, insertions, result);
    }

    if (result[childItemId].checked) someChildrenAreChecked = true;
    if (!result[childItemId].checked) allChildrenAreChecked = false;
  }

  result[itemId] = { checked: allChildrenAreChecked, indeterminate: someChildrenAreChecked && !allChildrenAreChecked };
}

function calculateItemIdsWithChildren(
  project: LayoutModel,
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
) {
  const result = new Set<string>();
  calculateItemIdsWithChildrenForModel(project, project.guid, allModels, insertions, result);
  return result;
}

function calculateItemIdsWithChildrenForModel(
  model: LayoutModel,
  itemId: string,
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
  result: Set<string>,
) {
  result.add(itemId);
  if (!modelCanHaveChildren(model)) return;

  for (const child of getChildren(model, allModels, insertions)) {
    const childItemId = `${itemId}-${child.guid}`;
    if (!result.has(childItemId)) {
      calculateItemIdsWithChildrenForModel(child, childItemId, allModels, insertions, result);
    }
  }
}

export function getAllDescendantsOfLayoutModel(
  model: LayoutModel,
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
) {
  const result = new Set<string>();
  getAllDescendantsRecursively(model, allModels, insertions, result);
  return result;
}

function getAllDescendantsRecursively(
  model: LayoutModel,
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
  result: Set<string>,
) {
  result.add(model.guid);

  if (modelCanHaveChildren(model)) {
    for (const child of getChildren(model, allModels, insertions)) {
      getAllDescendantsRecursively(child, allModels, insertions, result);
    }
  }
}

function modelCanHaveChildren(model: LayoutModel) {
  switch (model.category) {
    case ModelCategory.Project:
    case ModelCategory.IntermediateModel:
      return true;

    case ModelCategory.ComponentDesign:
    case ModelCategory.ComponentDesignData:
      return false;

    default:
      assertExhaustiveSwitch(model.category);
  }
}

const getChildren = (
  model: LayoutModel,
  allModels: { readonly [modelGuid: string]: LayoutModel },
  insertions: readonly Insertion[],
) =>
  uniq(
    insertions.filter((insertion) => insertion.parentGuid === model.guid).map((insertion) => insertion.objectGuid),
  ).map((objectGuid) => {
    if (!allModels[objectGuid]) {
      throw new Error(`An Insertion exists with objectGuid ${objectGuid}, but no corresponding model was found.`);
    }
    return allModels[objectGuid];
  });

function getLabelForModel(model: LayoutModel) {
  switch (model.category) {
    case ModelCategory.Project:
    case ModelCategory.ComponentDesign:
      return model.name;

    case ModelCategory.IntermediateModel:
      switch (model.type) {
        case 'building':
          return `Building ${model.name}`;

        case 'level':
          return `Level ${model.name}`;

        default:
          // We don't have exhaustiveness check here, so fallback to just returning the model name.
          return model.name;
      }

    case ModelCategory.ComponentDesignData:
      throw new Error('Component design data model not supported.');

    default:
      assertExhaustiveSwitch(model.category);
  }
}
